import {
  AlertCircle,
  Plus,
  Search,
  Soup,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import type { SwitcherUser } from "../components/AppSwitcher";
import { AppContainer } from "../components/AppContainer";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import type { AppName } from "../lib/apps";
import { realtimeFetch } from "../realtime/client";
import { useRealtime } from "../realtime/useRealtime";
import { filterDinnerMeals } from "./search";

export interface Meal {
  id: number;
  name: string;
  notes: string;
  score: number;
  skips: number;
}

interface DinnerListResponse {
  meals: Meal[];
  viewer: { skipped: boolean; confirmed: boolean };
  confirmationCount: number;
  requiredConfirmations: number;
  hasAdminConfirmation: boolean;
  mutationResult: "confirmed" | "unconfirmed" | "skipped" | "chosen" | null;
}

interface ApiErrorBody {
  error?: {
    message?: string;
    code?: string;
    fields?: Array<{ field: string; message: string }>;
  };
}

async function responseError(response: Response) {
  const body = await response.json().catch(() => null) as ApiErrorBody | null;
  return body?.error?.message ?? "Something went wrong. Please try again.";
}

type DinnerMutationError = Error & { fields?: Array<{ field: string; message: string }> };

async function mutationError(response: Response): Promise<DinnerMutationError> {
  const body = await response.json().catch(() => null) as ApiErrorBody | null;
  return Object.assign(new Error(body?.error?.message ?? "Could not save this meal."), { fields: body?.error?.fields });
}

interface DinnerLiveSaveCallbacks<T> {
  pending?: (snapshot: T) => void;
  saved?: (snapshot: T) => void;
  failed?: (error: unknown, snapshot: T) => void;
}

export interface DinnerLiveSaveQueue<T> {
  reserve: () => number;
  invalidate: () => void;
  enqueue: (snapshot: T, revision?: number) => Promise<boolean>;
  settle: () => Promise<void>;
  isIdle: () => boolean;
}

export function createDinnerLiveSaveQueue<T>(save: (snapshot: T) => Promise<void>, callbacks: DinnerLiveSaveCallbacks<T> = {}): DinnerLiveSaveQueue<T> {
  let tail = Promise.resolve();
  let latestRevision = 0;
  let pending = 0;
  return {
    reserve() { latestRevision += 1; return latestRevision; },
    invalidate() { latestRevision += 1; },
    enqueue(snapshot, reservedRevision) {
      const revision = reservedRevision ?? ++latestRevision;
      if (revision > latestRevision) latestRevision = revision;
      pending += 1;
      callbacks.pending?.(snapshot);
      const result = tail
        .then(() => save(snapshot))
        .then(() => {
          if (revision === latestRevision) callbacks.saved?.(snapshot);
          return true;
        }, error => {
          if (revision === latestRevision) callbacks.failed?.(error, snapshot);
          return false;
        })
        .finally(() => { pending -= 1; });
      tail = result.then(() => undefined);
      return result;
    },
    settle() { return tail; },
    isIdle() { return pending === 0; },
  };
}

function goTo(path: string) {
  window.location.assign(path);
}

function LoadingState({ label }: { label: string }) {
  return <div className="dinner-state" role="status"><span className="loading-dot" />{label}</div>;
}

function ErrorState({ message, retry }: { message: string; retry?: () => void }) {
  return (
    <div className="dinner-state error-state" role="alert">
      <AlertCircle size={22} aria-hidden="true" />
      <p>{message}</p>
      {retry && <button className="text-button" type="button" onClick={retry}>Try again</button>}
    </div>
  );
}

export interface DinnerEmptyStateProps {
  catalogTotal: number;
  meals: Meal[];
  error: string;
  retry?: () => void;
}

export function DinnerEmptyState({ catalogTotal, meals, error, retry }: DinnerEmptyStateProps) {
  if (error && meals.length === 0) return <ErrorState message={error} retry={retry} />;
  if (catalogTotal === 0) {
    return (
      <section className="empty-dinners">
        <span className="empty-dinner-icon"><Soup size={29} /></span>
        <h2>No meals yet</h2>
        <p>Add a meal to start your list.</p>
        <a className="primary-button" href="/dinner/new">Add meal</a>
      </section>
    );
  }
  // With a non-empty catalog the list always shows at least one meal.
  return null;
}

export function DinnerRow({ meal }: { meal: Meal }) {
  return (
    <li className="dinner-row-shell">
      <a className="dinner-row dinner-edit-target" href={`/dinner/${meal.id}/edit`} aria-label={`Edit ${meal.name}`}>
        <span className="dinner-name">{meal.name}</span>
      </a>
    </li>
  );
}

export function dinnerConfirmationStatus(confirmationCount: number, requiredConfirmations: number, hasAdminConfirmation: boolean) {
  const diners = `diner${requiredConfirmations === 1 ? "" : "s"}`;
  if (confirmationCount >= requiredConfirmations && !hasAdminConfirmation) {
    return `${confirmationCount} of ${requiredConfirmations} ${diners} confirmed · Waiting for an admin`;
  }
  return `Waiting for ${confirmationCount} of ${requiredConfirmations} ${diners}, including an admin`;
}

export function DinnerCandidate({
  meal,
  viewer,
  confirmationCount,
  requiredConfirmations,
  hasAdminConfirmation,
  isAdmin,
  busy,
  onSkip,
  onAdminSkip,
  onConfirm,
  onUnconfirm,
}: {
  meal: Meal;
  viewer: DinnerListResponse["viewer"];
  confirmationCount: number;
  requiredConfirmations: number;
  hasAdminConfirmation: boolean;
  isAdmin: boolean;
  busy: boolean;
  onSkip: (meal: Meal) => void;
  onAdminSkip: (meal: Meal) => void;
  onConfirm: (meal: Meal) => void;
  onUnconfirm: (meal: Meal) => void;
}) {
  const skipDisabled = viewer.skipped || meal.skips >= 3 || busy;
  return (
    <section className="dinner-candidate" aria-labelledby="dinner-candidate-name">
      <h2 id="dinner-candidate-name">{meal.name}</h2>
      {meal.notes && <p className="dinner-candidate-notes">{meal.notes}</p>}
      <div className="dinner-candidate-actions">
        <button className="dinner-skip-button" type="button" disabled={skipDisabled} aria-label={`Skip ${meal.name}`} onClick={() => onSkip(meal)}>Skip</button>
        {viewer.confirmed ? (
          <button className="dinner-eat-button is-confirmed" type="button" disabled={busy} aria-label={`Undo confirmation for ${meal.name}`} onClick={() => onUnconfirm(meal)}>Undo Eat</button>
        ) : (
          <button className="dinner-eat-button" type="button" disabled={busy} aria-label={`Confirm eating ${meal.name}`} onClick={() => onConfirm(meal)}>Eat</button>
        )}
      </div>
      <p className="dinner-confirmation-status" aria-live="polite">{dinnerConfirmationStatus(confirmationCount, requiredConfirmations, hasAdminConfirmation)}</p>
      {isAdmin && <button className="dinner-admin-skip-button" type="button" disabled={busy} aria-label={`Admin skip ${meal.name}`} onClick={() => onAdminSkip(meal)}>Admin skip</button>}
    </section>
  );
}

export function DeleteMealDialog({
  meal,
  busy,
  cancel,
  confirm,
}: {
  meal: Meal;
  busy: boolean;
  cancel: () => void;
  confirm: () => void;
}) {
  return <DeleteConfirmDialog title={<>Delete “{meal.name}”?</>} titleId="delete-meal-title" description="This permanently deletes the meal. This cannot be undone." descriptionId="delete-meal-description" busy={busy} onCancel={cancel} onConfirm={confirm} confirmLabel="Delete meal" />;
}

interface LoadResult {
  snapshot: DinnerListResponse;
  error: string;
}

const EMPTY_SNAPSHOT: DinnerListResponse = {
  meals: [],
  viewer: { skipped: false, confirmed: false },
  confirmationCount: 0,
  requiredConfirmations: 1,
  hasAdminConfirmation: false,
  mutationResult: null,
};

async function loadEverything(showSpinner: { spinner: (value: boolean) => void }): Promise<LoadResult> {
  showSpinner.spinner(true);
  try {
    const response = await fetch("/api/dinners/meals");
    if (!response.ok) throw new Error(await responseError(response));
    const payload = await response.json() as DinnerListResponse;
    return { snapshot: payload, error: "" };
  } catch (caught) {
    return { snapshot: EMPTY_SNAPSHOT, error: caught instanceof Error ? caught.message : "Could not load dinner." };
  } finally {
    showSpinner.spinner(false);
  }
}

interface DinnerListPageProps {
  user: SwitcherUser;
  onSignOut: () => Promise<void>;
}

function DinnerListPage({ user, onSignOut }: DinnerListPageProps) {
  const [snapshot, setSnapshot] = useState<DinnerListResponse>(EMPTY_SNAPSHOT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyIds, setBusyIds] = useState<Set<number>>(new Set());
  const [query, setQuery] = useState("");

  async function load(showSpinner = true) {
    if (showSpinner) {
      setLoading(true);
      const result = await loadEverything({ spinner: setLoading });
      setSnapshot(result.snapshot);
      setError(result.error);
      return;
    }
    // Silent refresh (realtime): preserve existing state, never flash a spinner.
    try {
      const response = await fetch("/api/dinners/meals");
      if (!response.ok) throw new Error("refresh failed");
      const payload = await response.json() as DinnerListResponse;
      setSnapshot(payload);
    } catch {
      // Ignore transient refresh errors; the next user action will surface them.
    }
  }

  useEffect(() => { void load(true); }, []);
  useRealtime("dinner", () => { void load(false); });

  function setBusy(mealId: number, on: boolean) {
    setBusyIds(current => {
      const next = new Set(current);
      if (on) next.add(mealId); else next.delete(mealId);
      return next;
    });
  }

  async function mutateCandidate(meal: Meal, method: "POST" | "DELETE", action: "confirm" | "skip" | "admin-skip") {
    setBusy(meal.id, true);
    setError("");
    try {
      const response = await realtimeFetch(`/api/dinners/meals/${meal.id}/${action}`, { method });
      if (!response.ok) throw new Error(await responseError(response));
      const payload = await response.json() as DinnerListResponse;
      setSnapshot(payload);
      setBusyIds(new Set());
    } catch (caught) {
      setBusy(meal.id, false);
      setError(caught instanceof Error ? caught.message : `Could not update ${meal.name}.`);
    }
  }

  const meals = snapshot.meals;
  const candidate = meals[0];
  const filteredMeals = filterDinnerMeals(meals, query);

  function renderEmpty() {
    return <DinnerEmptyState catalogTotal={meals.length} meals={meals} error={error} retry={() => void load(true)} />;
  }

  return (
    <AppContainer
      className="dinner-shell app-dinner"
        currentDestination="dinner"
        user={user}
        onSignOut={onSignOut}
        asHeading
        actions={<a className="add-dinner-button" href="/dinner/new" aria-label="Add meal"><Plus size={25} strokeWidth={2.4} /></a>}
      >
      {error && !loading && meals.length > 0 && <div className="inline-error" role="alert"><AlertCircle size={18} />{error}</div>}
      {!loading && candidate && (
        <DinnerCandidate
          meal={candidate}
          viewer={snapshot.viewer}
          confirmationCount={snapshot.confirmationCount}
          requiredConfirmations={snapshot.requiredConfirmations}
          hasAdminConfirmation={snapshot.hasAdminConfirmation}
          isAdmin={user.isAdmin}
          busy={busyIds.size > 0}
          onSkip={meal => void mutateCandidate(meal, "POST", "skip")}
          onAdminSkip={meal => void mutateCandidate(meal, "POST", "admin-skip")}
          onConfirm={meal => void mutateCandidate(meal, "POST", "confirm")}
          onUnconfirm={meal => void mutateCandidate(meal, "DELETE", "confirm")}
        />
      )}
      {meals.length > 0 && (
        <div className="dinner-search">
          <Search size={19} aria-hidden="true" />
          <input name="dinner-search" type="search" autoComplete="off" autoCapitalize="none" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search meals" aria-label="Search meals" />
          {query && <button className="search-clear-button" type="button" onClick={() => setQuery("")} aria-label="Clear dinner search"><X size={17} aria-hidden="true" /></button>}
        </div>
      )}
      {loading && meals.length === 0
        ? <LoadingState label="Loading dinner…" />
        : meals.length === 0
          ? renderEmpty()
          : filteredMeals.length === 0
            ? <div className="dinner-no-results" role="status">No meals match your search.</div>
            : (
            <ul className="dinner-list" aria-label="Dinner meals">
              {filteredMeals.map(meal => (
                <DinnerRow key={meal.id} meal={meal} />
              ))}
            </ul>
            )}

    </AppContainer>
  );
}

interface EditorForm {
  name: string;
  notes: string;
}

export function dinnerEditorValidation(form: EditorForm): Record<string, string> {
  const errors: Record<string, string> = {};
  const name = form.name.trim();
  if (!name) errors.name = "Name is required.";
  else if (name.length > 120) errors.name = "Name must be 120 characters or fewer.";
  if (form.notes.length > 600) errors.notes = "Notes must be 600 characters or fewer.";
  return errors;
}

export function DinnerEditorActions({ editing, saving, status, busy, deleting, onDelete }: { editing: boolean; saving: boolean; status: string; busy: boolean; deleting: boolean; onDelete: () => void }) {
  if (editing) {
    return (
      <div className="dinner-live-actions">
        <p className="save-status" role="status">{status}</p>
        <button className="editor-delete-button" type="button" onClick={onDelete} disabled={busy || deleting}><Trash2 size={17} />{deleting ? "Deleting…" : "Delete"}</button>
      </div>
    );
  }
  return <div className="form-actions dinner-form-actions"><button className="primary-button" type="submit" disabled={saving}>{saving ? "Saving…" : "Add meal"}</button></div>;
}

function DinnerEditorPage({ id, appName, user, onSignOut }: { id: number | null; appName: AppName; user: SwitcherUser; onSignOut: () => Promise<void> }) {
  const editing = id !== null;
  const [form, setForm] = useState<EditorForm>({ name: "", notes: "" });
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [pageError, setPageError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const timer = useRef<number | undefined>(undefined);
  const snapshot = useRef<EditorForm>({ name: "", notes: "" });
  const lastSaved = useRef("");
  const deletingRef = useRef(false);
  const leavingRef = useRef(false);
  const queue = useRef<DinnerLiveSaveQueue<EditorForm> | null>(null);

  if (!queue.current) {
    queue.current = createDinnerLiveSaveQueue(async value => {
      const response = await realtimeFetch(`/api/dinners/meals/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(value),
      });
      if (!response.ok) throw await mutationError(response);
    }, {
      pending: () => { if (!deletingRef.current) setStatus("Saving…"); },
      saved: value => {
        lastSaved.current = JSON.stringify(value);
        if (!deletingRef.current) {
          setStatus("Saved");
          setPageError("");
          setFieldErrors({});
        }
      },
      failed: caught => {
        if (!deletingRef.current) {
          setStatus("");
          setFieldErrors(Object.fromEntries(((caught as DinnerMutationError)?.fields ?? []).map(field => [field.field, field.message])));
          setPageError(caught instanceof Error ? caught.message : "Could not save this meal.");
        }
      },
    });
  }

  useEffect(() => {
    if (!editing) return;
    const controller = new AbortController();
    void (async () => {
      try {
        const response = await fetch(`/api/dinners/meals/${id}`, { signal: controller.signal });
        if (!response.ok) throw new Error(await responseError(response));
        const data = await response.json() as { meal: Meal };
        const loaded = { name: data.meal.name, notes: data.meal.notes };
        snapshot.current = loaded;
        lastSaved.current = JSON.stringify(loaded);
        setForm(loaded);
      } catch (caught) {
        if ((caught as Error).name !== "AbortError") setPageError(caught instanceof Error ? caught.message : "Could not load this meal.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [editing, id]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  function validate(value: EditorForm) {
    const errors = dinnerEditorValidation(value);
    setFieldErrors(errors);
    setPageError("");
    if (Object.keys(errors).length > 0) {
      setStatus("");
      return false;
    }
    return true;
  }

  async function enqueue(value: EditorForm, revision?: number) {
    const key = JSON.stringify(value);
    if (key === lastSaved.current && queue.current!.isIdle()) {
      setStatus("Saved");
      return true;
    }
    return queue.current!.enqueue(value, revision);
  }

  function schedule(value: EditorForm) {
    window.clearTimeout(timer.current);
    if (!editing) return;
    if (!validate(value)) {
      queue.current!.invalidate();
      return;
    }
    const revision = queue.current!.reserve();
    setStatus("Saving…");
    timer.current = window.setTimeout(() => {
      timer.current = undefined;
      void enqueue(value, revision);
    }, 500);
  }

  function setField(field: keyof EditorForm, value: string) {
    if (deletingRef.current || leavingRef.current) return;
    const next = { ...snapshot.current, [field]: value };
    snapshot.current = next;
    setForm(next);
    if (editing) schedule(next);
    else setFieldErrors(current => { const errors = { ...current }; delete errors[field]; return errors; });
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const current = snapshot.current;
    window.clearTimeout(timer.current);
    if (!validate(current)) {
      queue.current!.invalidate();
      return;
    }
    if (editing) {
      const revision = queue.current!.reserve();
      setStatus("Saving…");
      await enqueue(current, revision);
      return;
    }
    setSaving(true);
    setPageError("");
    setFieldErrors({});
    try {
      const response = await realtimeFetch("/api/dinners/meals", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(current),
      });
      if (!response.ok) throw await mutationError(response);
      goTo("/dinner/");
    } catch (caught) {
      setFieldErrors(Object.fromEntries(((caught as DinnerMutationError)?.fields ?? []).map(field => [field.field, field.message])));
      setPageError(caught instanceof Error ? caught.message : "Could not save this meal.");
      setSaving(false);
    }
  }

  async function back() {
    if (!editing || deletingRef.current || leavingRef.current) return;
    window.clearTimeout(timer.current);
    const current = snapshot.current;
    if (!validate(current)) {
      queue.current!.invalidate();
      return;
    }
    leavingRef.current = true;
    const revision = queue.current!.reserve();
    setStatus("Saving…");
    const saved = await enqueue(current, revision);
    if (saved) goTo("/dinner/");
    else leavingRef.current = false;
  }

  async function deleteMeal() {
    if (!editing || !id || deleting) return;
    deletingRef.current = true;
    setDeleting(true);
    setPageError("");
    setStatus("");
    window.clearTimeout(timer.current);
    queue.current!.invalidate();
    await queue.current!.settle();
    try {
      const response = await realtimeFetch(`/api/dinners/meals/${id}`, { method: "DELETE" });
      if (!response.ok) throw new Error(await responseError(response));
      goTo("/dinner/");
    } catch (caught) {
      deletingRef.current = false;
      setPageError(caught instanceof Error ? caught.message : "Could not delete this meal.");
      setShowDeleteConfirmation(false);
      setDeleting(false);
    }
  }

  const editorTitle = editing ? "Edit meal" : "New meal";

  if (loading) return <AppContainer className="editor-shell dinner-editor-shell app-dinner" currentDestination={appName} user={user} onSignOut={onSignOut} backHref="/dinner/" backLabel="Back to Dinner" title={editorTitle} asHeading><LoadingState label="Loading meal…" /></AppContainer>;
  if (pageError && editing && !form.name) return <AppContainer className="editor-shell dinner-editor-shell app-dinner" currentDestination={appName} user={user} onSignOut={onSignOut} backHref="/dinner/" backLabel="Back to Dinner" title={editorTitle} asHeading><ErrorState message={pageError} /></AppContainer>;

  return (
    <AppContainer className="editor-shell dinner-editor-shell app-dinner" currentDestination={appName} user={user} onSignOut={onSignOut} {...(editing ? { onBack: () => void back() } : { backHref: "/dinner/" })} backLabel="Back to Dinner" title={editorTitle} asHeading>
      <form className="dinner-form" autoComplete="off" onSubmit={save} noValidate>
        {pageError && <div className="form-error" role="alert"><AlertCircle size={18} />{pageError}</div>}

        <label className="form-field">
          <span>Name</span>
          <input autoFocus={!editing} name="dinner-name" autoComplete="off" autoCapitalize="sentences" value={form.name} onChange={event => setField("name", event.target.value)} maxLength={120} placeholder="What's for dinner?" aria-invalid={Boolean(fieldErrors.name)} aria-describedby={fieldErrors.name ? "name-error" : undefined} />
          {fieldErrors.name && <small id="name-error" className="field-error">{fieldErrors.name}</small>}
        </label>

        <label className="form-field">
          <span>Notes</span>
          <textarea name="dinner-notes" autoComplete="off" autoCapitalize="sentences" value={form.notes} onChange={event => setField("notes", event.target.value)} maxLength={600} rows={4} placeholder="Side dishes, who's cooking, where the recipe lives…" aria-invalid={Boolean(fieldErrors.notes)} aria-describedby={fieldErrors.notes ? "notes-error" : undefined} />
          {fieldErrors.notes && <small id="notes-error" className="field-error">{fieldErrors.notes}</small>}
        </label>

        <DinnerEditorActions editing={editing} saving={saving} status={status} busy={false} deleting={deleting} onDelete={() => setShowDeleteConfirmation(true)} />
      </form>
      {showDeleteConfirmation && editing && (
        <DeleteMealDialog
          meal={{ id, name: form.name, notes: form.notes, score: 0, skips: 0 }}
          busy={deleting}
          cancel={() => !deleting && setShowDeleteConfirmation(false)}
          confirm={() => void deleteMeal()}
        />
      )}
    </AppContainer>
  );
}

export function DinnerApp({ route, appName, user, onSignOut }: { route: string; appName: AppName; user: SwitcherUser; onSignOut: () => Promise<void> }) {
  if (route === "/dinner/new") return <DinnerEditorPage id={null} appName={appName} user={user} onSignOut={onSignOut} />;
  const editMatch = /^\/dinner\/(\d+)\/edit$/.exec(route);
  if (editMatch) return <DinnerEditorPage id={Number(editMatch[1]!)} appName={appName} user={user} onSignOut={onSignOut} />;
  return <DinnerListPage user={user} onSignOut={onSignOut} />;
}
